"use client";
import type React from "react"
import { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import CourseDetailsStep from "./steps/CourseDetailsStep"
import CurriculumStep from "./steps/CurriculumStep"
import CertificateStep from "./steps/CertificateStep"
import PaymentDetailsStep, { PaymentDetailsStepRef } from "./steps/PaymentDetailsStep"
import AdditionalDetailsStep, { AdditionalDetailsStepRef } from "./steps/AdditionalDetailsStep"
import useAuth from "./../../hooks/useAuth"
import { createCourse, getCourseById, getCoursePriceByCourseId, getCourseCertificatesByCourseId } from "../../api/courseService"
import Modal from "../../components/common/Modal"
import { ArrowLeft, Check, Save } from "lucide-react"

interface CourseData {
  title: string;
  description: string;
  language: string;
  coverImage: File | null;
  availableOnDesktop: boolean;
  modules: any[];
  certificates: any[];
  pricing: any;
  requirements: string[];
  enableCertificate: boolean;
  additional: any;
  _id: string | null;
  courseType: 'Course' | 'Webinar';
  webinarChannel: string | null;
}

const CreateCourse: React.FC = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [courseData, setCourseData] = useState<CourseData>({
    title: "",
    description: "",
    language: "English",
    coverImage: null,
    availableOnDesktop: true,
    modules: [],
    certificates: [],
    pricing: {},
    requirements: [],
    enableCertificate: false,
    additional: {},
    _id: null,
    courseType: "Course",
    webinarChannel: null
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const { user } = useAuth()
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  
  const certificateStepRef = useRef<{ saveCertificate: () => Promise<void> }>(null)
  const paymentDetailsStepRef = useRef<PaymentDetailsStepRef>(null)
  const additionalDetailsStepRef = useRef<AdditionalDetailsStepRef>(null)

  const steps = [
    { id: 1, title: "Course details", completed: currentStep > 1, current: currentStep === 1 },
    { id: 2, title: "Curriculum", completed: currentStep > 2, current: currentStep === 2 },
    { id: 3, title: "Certificate", completed: currentStep > 3, current: currentStep === 3 },
    { id: 4, title: "Payment details", completed: currentStep > 4, current: currentStep === 4 },
    { id: 5, title: "Additional details", completed: currentStep > 5, current: currentStep === 5 },
  ]

  // Function to fetch certificate data
  const fetchCertificateData = async () => {
    if (!courseData._id) return;
    
    try {
      const certificatesResponse = await getCourseCertificatesByCourseId(courseData._id);
      if (certificatesResponse.status === 200 && certificatesResponse.data) {
        setCourseData(prev => ({
          ...prev,
          certificates: certificatesResponse.data,
          enableCertificate: certificatesResponse.data.length > 0
        }));
      }
    } catch (error) {
      console.error("Error fetching certificate data:", error);
    }
  };

  // Refresh pricing data when returning to Payment Details step
  useEffect(() => {
    if (currentStep === 4 && courseData._id) {
      const refreshPricingData = async () => {
        try {
          if (courseData._id) {
            const pricingResponse = await getCoursePriceByCourseId(courseData._id as string);
            if (pricingResponse.status === 200 && pricingResponse.data) {
              setCourseData(prev => ({
                ...prev,
                pricing: pricingResponse.data
              }));
            }
          }
        } catch (error) {
          console.error("Error refreshing pricing data:", error);
        }
      };
      
      refreshPricingData();
    }
  }, [currentStep, courseData._id]);

  // Fetch certificate data when step changes to 3 and course has an ID
  useEffect(() => {
    if (currentStep === 3 && courseData._id) {
      fetchCertificateData();
    }
  }, [currentStep, courseData._id, fetchCertificateData]);

  const handleNext = () => {
    if (currentStep === 2) {
      // Check if there are any lessons, quizzes, or documents in any module
      const hasContent = courseData.modules?.some(module => 
        module.lessons && Array.isArray(module.lessons) && module.lessons.length > 0
      );
      
      // Also check if any module has lessons with a length property
      const hasLegacyContent = courseData.modules?.some(module => 
        module.lessons && module.lessons.length > 0
      );
      
      if (!hasContent && !hasLegacyContent) {
        setError("Please create at least one lesson, quiz, or document in a module before proceeding to the next step.");
        return;
      }
    }
    
    if (currentStep < 5) {
      setError(null);
      setCurrentStep(currentStep + 1);
    }
  }

  const handleBack = async () => {
    if (currentStep === 5 && additionalDetailsStepRef.current) {
      // If going back from Additional Details step, save any changes first
      try {
        setLoading(true);
        await additionalDetailsStepRef.current.saveAdditionalDetails("Draft");
      } catch (error: any) {
        setError(error.message);
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }
    
    if (currentStep === 4 && paymentDetailsStepRef.current) {
      // Check if there are unsaved changes
      if (paymentDetailsStepRef.current.hasUnsavedChanges()) {
        const shouldSave = window.confirm(
          "You have unsaved changes in the payment details. Do you want to save them before going back?"
        );
        
        if (shouldSave) {
          try {
            setLoading(true);
            await paymentDetailsStepRef.current.savePaymentDetails();
          } catch (error: any) {
            setError(error.message);
            setLoading(false);
            return;
          } finally {
            setLoading(false);
          }
        }
      }
    }
    
    // Fetch certificate data when going back to certificate step
    if (currentStep === 4 && courseData._id) {
      try {
        setLoading(true);
        const certificatesResponse = await getCourseCertificatesByCourseId(courseData._id);
        if (certificatesResponse.status === 200 && certificatesResponse.data) {
          setCourseData(prev => ({
            ...prev,
            certificates: certificatesResponse.data,
            enableCertificate: certificatesResponse.data.length > 0
          }));
        }
      } catch (error: any) {
        console.error("Error fetching certificate data:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }
    
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setLoading(true)
      setError(null)
      
      console.log("Saving course as draft with data:", courseData)
      
      let courseId = courseData._id
      if (!courseId) {
        console.log("Creating new course...")
        const response = await createCourse({
          file: courseData.coverImage!,
          title: courseData.title,
          description: courseData.description,
          language: courseData.language,
          desktopAvailable: courseData.availableOnDesktop,
          created_by: user?._id || "",
          courseType: courseData.courseType,
          webinarChannel: courseData.courseType === "Webinar" ? courseData.webinarChannel : null
        })
        console.log("Create course response:", response)
        
        if (response.status >= 400) {
          throw new Error(response.message || "Failed to create course")
        }
        courseId = response.data?._id
        setCourseData(prev => ({ ...prev, _id: courseId }))
      }
      
      if (currentStep === 5 && additionalDetailsStepRef.current) {
        console.log("Saving additional details with status Draft...")
        await additionalDetailsStepRef.current.saveAdditionalDetails("Draft")
        console.log("Additional details saved successfully")
        
        // Re-fetch the updated course data
        if (courseId) {
          const updatedCourseResponse = await getCourseById(courseId);
          if (updatedCourseResponse.status === 200 && updatedCourseResponse.data) {
            const fetchedData = updatedCourseResponse.data;
            setCourseData(prev => ({
              ...prev,
              additional: fetchedData.additional || {}
            }));
          }
        }
      }
      
      setSuccessMessage("Course saved as draft successfully!")
      setTimeout(() => {
        setSuccessMessage(null)
      }, 1000)
    } catch (error: any) {
      console.error("Error saving draft:", error)
      setError(error.message || "Failed to save draft. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handlePublish = async () => {
    try {
      setLoading(true)
      setError(null)
      
      console.log("Publishing course with data:", courseData)
      
      let courseId = courseData._id
      if (!courseId) {
        console.log("Creating new course...")
        const response = await createCourse({
          file: courseData.coverImage!,
          title: courseData.title,
          description: courseData.description,
          language: courseData.language,
          desktopAvailable: courseData.availableOnDesktop,
          created_by: user?._id || "",
          courseType: courseData.courseType,
          webinarChannel: courseData.courseType === "Webinar" ? courseData.webinarChannel : null
        })
        console.log("Create course response:", response)
        
        if (response.status >= 400) {
          throw new Error(response.message || "Failed to create course")
        }
        courseId = response.data?._id
        setCourseData(prev => ({ ...prev, _id: courseId }))
      }
      
      if (currentStep === 5 && additionalDetailsStepRef.current) {
        console.log("Saving additional details with status Published...")
        await additionalDetailsStepRef.current.saveAdditionalDetails("Published")
        console.log("Additional details saved successfully")
        
        // Re-fetch the updated course data
        if (courseId) {
          const updatedCourseResponse = await getCourseById(courseId);
          if (updatedCourseResponse.status === 200 && updatedCourseResponse.data) {
            const fetchedData = updatedCourseResponse.data;
            setCourseData(prev => ({
              ...prev,
              additional: fetchedData.additional || {}
            }));
          }
        }
      }
      
      setShowSuccessModal(true)
    } catch (error: any) {
      console.error("Error publishing course:", error)
      setError(error.message || "Failed to publish course. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleCertificateNext = async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (certificateStepRef.current) {
        await certificateStepRef.current.saveCertificate()
        
        // Re-fetch the updated course data
        if (courseData._id) {
          const updatedCourseResponse = await getCourseById(courseData._id);
          if (updatedCourseResponse.status === 200 && updatedCourseResponse.data) {
            const fetchedData = updatedCourseResponse.data;
            setCourseData(prev => ({
              ...prev,
              certificates: fetchedData.certificates || []
            }));
          }
        }
        
        handleNext()
      } else {
        handleNext()
      }
    } catch (err) {
      console.error("Error saving certificate:", err)
      setError(err instanceof Error ? err.message : "Failed to save certificate")
    } finally {
      setLoading(false)
    }
  }

  const handlePaymentDetailsNext = async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (paymentDetailsStepRef.current) {
        await paymentDetailsStepRef.current.savePaymentDetails()
        
        // Re-fetch the updated pricing data
        if (courseData._id) {
          try {
            const pricingResponse = await getCoursePriceByCourseId(courseData._id);
            if (pricingResponse.status === 200 && pricingResponse.data) {
              setCourseData(prev => ({
                ...prev,
                pricing: pricingResponse.data
              }));
            }
          } catch (pricingError) {
            console.error("Error fetching updated pricing data:", pricingError);
          }
        }
        
        handleNext()
      } else {
        handleNext()
      }
    } catch (err) {
      console.error("Error saving payment details:", err)
      setError(err instanceof Error ? err.message : "Failed to save payment details")
    } finally {
      setLoading(false)
    }
  }

  const handleGoToDashboard = () => {
    setShowSuccessModal(false)
    navigate("/courses")
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CourseDetailsStep 
            key={`step1-${courseData._id || 'new'}`}
            data={courseData} 
            onChange={setCourseData} 
            onNext={handleNext}
            setLoading={setLoading}
            setError={setError}
            userId={user?._id || ""}
          />
        )
      case 2:
        return <CurriculumStep 
          key={`step2-${courseData._id || 'new'}`}
          data={courseData} 
          onChange={setCourseData} 
          onNext={handleNext} 
        />
      case 3:
        return (
          <CertificateStep 
            key={`step3-${courseData._id || 'new'}`}
            ref={certificateStepRef}
            data={courseData} 
            onChange={setCourseData}
            onNext={handleNext}
            setLoading={setLoading}
            setError={setError}
            isEditMode={false}
          />
        )
      case 4:
        return (
          <PaymentDetailsStep 
            key={`step4-${courseData._id || 'new'}-${courseData.pricing?._id || 'no-pricing'}`}
            ref={paymentDetailsStepRef}
            data={courseData} 
            onChange={setCourseData}
          />
        )
      case 5:
        return (
          <AdditionalDetailsStep 
            key={`step5-${courseData._id || 'new'}`}
            ref={additionalDetailsStepRef}
            data={courseData} 
            onChange={setCourseData}
            onNavigateAfterSave={() => navigate("/courses")}
          />
        )
      default:
        return <CourseDetailsStep 
          key={`step1-${courseData._id || 'new'}`}
          data={courseData} 
          onChange={setCourseData} 
          onNext={handleNext} 
          setLoading={setLoading} 
          setError={setError} 
          userId={user?._id || ""} 
        />
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 lg:px-6 py-3 shadow-sm">
        <div className="flex items-center">
          <button
            onClick={() => navigate("/courses")}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
          >
            <ArrowLeft className="mr-1 sm:mr-2" size={16} />
            <span className="hidden sm:inline text-sm">Go to dashboard</span>
            <span className="sm:hidden text-sm">Back</span>
          </button>
        </div>
      </div>
      
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-3">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xs sm:text-sm font-semibold text-gray-900">Progress</h2>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {Math.round((currentStep / steps.length) * 100)}% Complete
            </span>
          </div>
          <div className="relative">
            <div className="flex justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex flex-col items-center relative z-10">
                  <div
                    className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                      step.completed
                        ? "bg-green-500 text-white shadow-md"
                        : step.current
                        ? "bg-blue-600 text-white shadow-md ring-2 ring-blue-200"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {step.completed ? (
                      <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                    ) : (
                      <span>{step.id}</span>
                    )}
                  </div>
                  <span className={`mt-1 sm:mt-2 text-xs font-medium text-center max-w-12 sm:max-w-16 lg:max-w-none leading-tight ${
                    step.current ? "text-blue-600" : "text-gray-500"
                  }`}>
                    {step.title}
                  </span>
                </div>
              ))}
            </div>
            <div className="absolute top-3 sm:top-4 left-3 sm:left-4 right-3 sm:right-4 h-0.5 bg-gray-200 -z-0">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500 ease-out"
                style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>      
      
      <div className="max-w-4xl mx-auto p-3 sm:p-4 lg:p-6">
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-3 sm:p-4 lg:p-8">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
                <div className="bg-red-100 rounded-full p-1 flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-red-800">Error occurred</h4>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}
            
            {successMessage && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-start space-x-3">
                <div className="bg-green-100 rounded-full p-1 flex-shrink-0">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-800">Success!</h4>
                  <p className="text-sm text-green-700 mt-1">{successMessage}</p>
                </div>
              </div>
            )}
            
            {renderCurrentStep()}
            
            <div className="flex flex-col sm:flex-row justify-between mt-8 pt-6 border-t border-gray-200 space-y-3 sm:space-y-0">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className={`px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-medium ${
                  currentStep === 1 ? "invisible" : ""
                } w-full sm:w-auto justify-center flex items-center space-x-2`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back</span>
              </button>
              
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                {currentStep === 5 && (
                  <button
                    onClick={handleSaveDraft}
                    disabled={loading}
                    className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center flex items-center space-x-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>{loading ? 'Saving...' : 'Save as Draft'}</span>
                  </button>
                )}
                
                {currentStep === 1 ? (
                  <button
                    onClick={() => {
                      const form = document.querySelector('#course-details-form')
                      if (form) (form as HTMLFormElement).requestSubmit()
                    }}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none w-full sm:w-auto justify-center flex items-center space-x-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>{courseData._id ? 'Updating...' : 'Creating...'}</span>
                      </>
                    ) : (
                      <>
                        <span>{courseData._id ? 'Update & Next' : 'Continue'}</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                ) : currentStep === 3 ? (
                  <button
                    onClick={handleCertificateNext}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none w-full sm:w-auto justify-center flex items-center space-x-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <span>Continue</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                ) : currentStep === 4 ? (
                  <button
                    onClick={handlePaymentDetailsNext}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none w-full sm:w-auto justify-center flex items-center space-x-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <span>Continue</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                ) : currentStep < 5 ? (
                  <button
                    onClick={handleNext}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold w-full sm:w-auto justify-center flex items-center space-x-2"
                  >
                    <span>Continue</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : currentStep === 5 ? (
                  <button
                    onClick={handlePublish}
                    className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none w-full sm:w-auto justify-center flex items-center space-x-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Publishing...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Publish Course</span>
                      </>
                    )}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {}}
        className="w-full max-w-md"
      >
        <div className="text-center p-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Course Published Successfully!</h3>
          <p className="text-gray-600 mb-6">
            Your course "{courseData.title}" has been published and is now available for students.
          </p>
          <button
            onClick={handleGoToDashboard}
            className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>Go to Dashboard</span>
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default CreateCourse